import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/apiClient';
import './SettingsPanel.css';

const CUISINE_KEYS = [
  'burgers', 'pizza', 'asian', 'sushi', 'coffee',
  'bakery', 'georgian', 'kazakh', 'dessert', 'healthy',
  'korean', 'indian', 'italian', 'breakfast', 'chicken',
  'seafood', 'vegan', 'ramen', 'streetfood', 'bbq',
] as const;

type DaySchedule = { open: string; close: string };
type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
type OpeningHours = Partial<Record<DayKey, DaySchedule | null>>;

interface Props {
  restaurantId: string;
  currentLogoUrl?: string | null;
  currentDeliveryMode?: 'PICKUP' | 'TABLE_SERVICE';
  currentLat?: number | null;
  currentLng?: number | null;
  currentOpeningHours?: OpeningHours;
  currentDineInSupported?: boolean;
  currentAcceptsPreorders?: boolean;
  currentCuisine?: string | null;
  currentTimezone?: string | null;
  currentCoverImageUrl?: string | null;
  currentIsFeatured?: boolean;
  currentTableCount?: number;
  onLogoUpdated: (url: string | null) => void;
  onDeliveryModeUpdated: (mode: 'PICKUP' | 'TABLE_SERVICE') => void;
  onLatLngUpdated: (lat: number | null, lng: number | null) => void;
  onOpeningHoursUpdated: (hours: OpeningHours) => void;
  onDineInUpdated: (value: boolean) => void;
  onPreorderUpdated: (value: boolean) => void;
  onCuisineUpdated?: (value: string | null) => void;
  onTimezoneUpdated?: (value: string | null) => void;
  onCoverImageUrlUpdated?: (value: string | null) => void;
  onIsFeaturedUpdated?: (value: boolean) => void;
  onTableCountUpdated?: (value: number) => void;
}

export default function SettingsPanel({
  restaurantId,
  currentLogoUrl,
  currentDeliveryMode = 'PICKUP',
  currentLat = null,
  currentLng = null,
  currentOpeningHours = {},
  currentDineInSupported = false,
  currentAcceptsPreorders = false,
  currentCuisine = null,
  currentTimezone = null,
  currentCoverImageUrl = null,
  currentIsFeatured = false,
  currentTableCount = 0,
  onLogoUpdated,
  onDeliveryModeUpdated,
  onLatLngUpdated,
  onOpeningHoursUpdated,
  onDineInUpdated,
  onPreorderUpdated,
  onCuisineUpdated,
  onTimezoneUpdated,
  onCoverImageUrlUpdated,
  onIsFeaturedUpdated,
  onTableCountUpdated,
}: Props) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [deliveryMode, setDeliveryMode] = useState<'PICKUP' | 'TABLE_SERVICE'>(currentDeliveryMode);
  const [savingMode, setSavingMode] = useState(false);
  const [modeError, setModeError] = useState('');

  // Phase 15 — local edit state for the 4 new sections
  const DAYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const [latStr, setLatStr] = useState<string>(currentLat == null ? '' : String(currentLat));
  const [lngStr, setLngStr] = useState<string>(currentLng == null ? '' : String(currentLng));
  const [hours, setHours] = useState<OpeningHours>(currentOpeningHours || {});
  const [dineIn, setDineIn] = useState<boolean>(currentDineInSupported);
  const [preorder, setPreorder] = useState<boolean>(currentAcceptsPreorders);
  const [savingGeo, setSavingGeo] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [savingDineIn, setSavingDineIn] = useState(false);
  const [savingPreorder, setSavingPreorder] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [hoursError, setHoursError] = useState('');

  // Phase 16 — cuisine + timezone editors
  const [cuisineDraft, setCuisineDraft] = useState<string>(currentCuisine ?? '');
  const [timezoneDraft, setTimezoneDraft] = useState<string>(currentTimezone ?? '');
  const [lastValidTimezone, setLastValidTimezone] = useState<string>(currentTimezone ?? '');
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  const [cuisineSaving, setCuisineSaving] = useState(false);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [cuisineError, setCuisineError] = useState<string | null>(null);

  // Phase 17 — coverImageUrl + isFeatured editors
  const [coverImageUrlDraft, setCoverImageUrlDraft] = useState<string>(currentCoverImageUrl ?? '');
  const [savingCoverImageUrl, setSavingCoverImageUrl] = useState(false);
  const [coverImageUrlError, setCoverImageUrlError] = useState<string | null>(null);
  const [isFeaturedDraft, setIsFeaturedDraft] = useState<boolean>(!!currentIsFeatured);
  const [savingIsFeatured, setSavingIsFeatured] = useState(false);
  const [isFeaturedError, setIsFeaturedError] = useState<string | null>(null);

  // Phase 18 — tableCount editor
  const [tableCountDraft, setTableCountDraft] = useState<number>(currentTableCount ?? 0);
  const [savingTableCount, setSavingTableCount] = useState(false);
  const [tableCountError, setTableCountError] = useState<string | null>(null);

  const validTimezones = useMemo(() => {
    try {
      const intlAny = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
      if (typeof intlAny.supportedValuesOf === 'function') {
        return new Set(intlAny.supportedValuesOf('timeZone'));
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    setDeliveryMode(currentDeliveryMode);
  }, [currentDeliveryMode]);

  useEffect(() => {
    setLatStr(currentLat == null ? '' : String(currentLat));
    setLngStr(currentLng == null ? '' : String(currentLng));
    setHours(currentOpeningHours || {});
    setDineIn(currentDineInSupported);
    setPreorder(currentAcceptsPreorders);
  }, [currentLat, currentLng, currentOpeningHours, currentDineInSupported, currentAcceptsPreorders]);

  useEffect(() => {
    setCuisineDraft(currentCuisine ?? '');
    setTimezoneDraft(currentTimezone ?? '');
    setLastValidTimezone(currentTimezone ?? '');
    setTimezoneError(null);
    setCuisineError(null);
    setCoverImageUrlDraft(currentCoverImageUrl ?? '');
    setIsFeaturedDraft(!!currentIsFeatured);
    setCoverImageUrlError(null);
    setIsFeaturedError(null);
    setTableCountDraft(currentTableCount ?? 0);
    setTableCountError(null);
  }, [restaurantId, currentCuisine, currentTimezone, currentCoverImageUrl, currentIsFeatured, currentTableCount]);

  const handleTimezoneBlur = () => {
    const trimmed = timezoneDraft.trim();
    if (trimmed === '') {
      setTimezoneError(null);
      return;
    }
    if (validTimezones && !validTimezones.has(trimmed)) {
      setTimezoneError(t('admin.restaurant.timezoneInvalid'));
      setTimezoneDraft(lastValidTimezone);
      return;
    }
    setTimezoneError(null);
  };

  const saveCuisine = async () => {
    setCuisineSaving(true);
    setCuisineError(null);
    const trimmed = cuisineDraft.trim();
    const body = { cuisine: trimmed === '' ? null : trimmed };
    const previous = currentCuisine ?? null;
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, body);
      onCuisineUpdated?.(body.cuisine);
    } catch (err: any) {
      setCuisineDraft(previous ?? '');
      setCuisineError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveFailed'));
    } finally {
      setCuisineSaving(false);
    }
  };

  const saveCoverImageUrl = async () => {
    setSavingCoverImageUrl(true);
    setCoverImageUrlError(null);
    const trimmed = coverImageUrlDraft.trim();
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { coverImageUrl: trimmed || null });
      onCoverImageUrlUpdated?.(trimmed || null);
    } catch (err: any) {
      setCoverImageUrlError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveFailed'));
    } finally {
      setSavingCoverImageUrl(false);
    }
  };

  const saveIsFeatured = async () => {
    setSavingIsFeatured(true);
    setIsFeaturedError(null);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { isFeatured: isFeaturedDraft });
      onIsFeaturedUpdated?.(isFeaturedDraft);
    } catch (err: any) {
      setIsFeaturedError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveFailed'));
    } finally {
      setSavingIsFeatured(false);
    }
  };

  const handleSaveTableCount = async () => {
    setSavingTableCount(true);
    setTableCountError(null);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { tableCount: tableCountDraft });
      onTableCountUpdated?.(tableCountDraft);
    } catch (err: any) {
      setTableCountError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveFailed'));
    } finally {
      setSavingTableCount(false);
    }
  };

  const saveTimezone = async () => {
    if (timezoneError) return;
    setTimezoneSaving(true);
    const trimmed = timezoneDraft.trim();
    const body = { timezone: trimmed === '' ? null : trimmed };
    const previous = currentTimezone ?? null;
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, body);
      setLastValidTimezone(trimmed);
      onTimezoneUpdated?.(body.timezone);
    } catch (err: any) {
      setTimezoneDraft(previous ?? '');
      setLastValidTimezone(previous ?? '');
      setTimezoneError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveFailed'));
    } finally {
      setTimezoneSaving(false);
    }
  };

  const handleDeliveryModeChange = async (newMode: 'PICKUP' | 'TABLE_SERVICE') => {
    if (newMode === deliveryMode) return;
    setModeError('');
    setSavingMode(true);
    const prev = deliveryMode;
    setDeliveryMode(newMode);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { deliveryMode: newMode });
      onDeliveryModeUpdated(newMode);
    } catch (err: any) {
      setDeliveryMode(prev);
      setModeError(err.response?.data?.error || 'Failed to update delivery mode');
    } finally {
      setSavingMode(false);
    }
  };

  const handleSaveGeo = async () => {
    setGeoError('');
    setSavingGeo(true);
    try {
      const lat = latStr.trim() === '' ? null : Number(latStr);
      const lng = lngStr.trim() === '' ? null : Number(lngStr);
      if (lat !== null && (!Number.isFinite(lat) || lat < -90 || lat > 90)) {
        throw new Error('lat must be a number in [-90, 90]');
      }
      if (lng !== null && (!Number.isFinite(lng) || lng < -180 || lng > 180)) {
        throw new Error('lng must be a number in [-180, 180]');
      }
      await api.patch(`/api/restaurants/${restaurantId}`, { lat, lng });
      onLatLngUpdated(lat, lng);
    } catch (err: any) {
      setGeoError(err?.response?.data?.error || err?.message || t('admin.restaurant.saveError'));
    } finally {
      setSavingGeo(false);
    }
  };

  const handleSaveHours = async () => {
    setHoursError('');
    setSavingHours(true);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { openingHours: hours });
      onOpeningHoursUpdated(hours);
    } catch (err: any) {
      setHoursError(err?.response?.data?.error || t('admin.restaurant.saveError'));
    } finally {
      setSavingHours(false);
    }
  };

  const toggleDayOpen = (day: DayKey, isOpen: boolean) => {
    setHours(prev => ({
      ...prev,
      [day]: isOpen ? (prev[day] || { open: '09:00', close: '22:00' }) : null,
    }));
  };

  const setDayTime = (day: DayKey, field: 'open' | 'close', value: string) => {
    setHours(prev => {
      const cur = prev[day] || { open: '09:00', close: '22:00' };
      return { ...prev, [day]: { ...cur, [field]: value } };
    });
  };

  const handleToggleDineIn = async (next: boolean) => {
    const prev = dineIn;
    setDineIn(next);
    setSavingDineIn(true);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { dineInSupported: next });
      onDineInUpdated(next);
    } catch {
      setDineIn(prev);
    } finally {
      setSavingDineIn(false);
    }
  };

  const handleTogglePreorder = async (next: boolean) => {
    const prev = preorder;
    setPreorder(next);
    setSavingPreorder(true);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { acceptsPreorders: next });
      onPreorderUpdated(next);
    } catch {
      setPreorder(prev);
    } finally {
      setSavingPreorder(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await api.post<{ url: string }>('/api/upload?folder=logos', form);
      const { url } = uploadRes.data;
      await api.patch(`/api/restaurants/${restaurantId}`, { logoUrl: url });
      onLogoUpdated(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError('');
    setUploading(true);
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, { logoUrl: null });
      onLogoUpdated(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="settings-panel">
      <div className="settings-section">
        <h3>{t('admin.restaurantLogo', 'Логотип ресторана')}</h3>

        {currentLogoUrl
          ? <img src={currentLogoUrl} alt="Logo" className="logo-preview" />
          : <div className="logo-placeholder"></div>
        }

        <div className="logo-actions">
          <button className="settings-btn settings-btn--primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? '...' : t('admin.uploadLogo', 'Загрузить логотип')}
          </button>
          {currentLogoUrl && (
            <button className="settings-btn settings-btn--danger" onClick={handleRemove} disabled={uploading}>
              {t('admin.removeLogo', 'Удалить')}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        </div>

        {uploading && <p className="settings-uploading">⏳ {t('admin.uploading', 'Загружаем...')}</p>}
        {error && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{error}</p>}
        <p className="settings-upload-hint">JPG, PNG, WebP, GIF — до 10MB</p>
      </div>

      <div className="settings-section">
        <h3>{t('admin.deliveryMode', 'Способ выдачи заказа')}</h3>
        <div className="delivery-mode-group">
          <label className="delivery-mode-option">
            <input
              type="radio"
              name="deliveryMode"
              value="PICKUP"
              checked={deliveryMode === 'PICKUP'}
              disabled={savingMode}
              onChange={() => handleDeliveryModeChange('PICKUP')}
            />
            <span>{t('admin.deliveryModePickup', 'Самовывоз')}</span>
          </label>
          <label className="delivery-mode-option">
            <input
              type="radio"
              name="deliveryMode"
              value="TABLE_SERVICE"
              checked={deliveryMode === 'TABLE_SERVICE'}
              disabled={savingMode}
              onChange={() => handleDeliveryModeChange('TABLE_SERVICE')}
            />
            <span>{t('admin.deliveryModeTableService', 'Обслуживание у стола')}</span>
          </label>
        </div>
        <p className="settings-upload-hint">{t('admin.deliveryModeHint', 'Определяет текст push-уведомления «заказ готов»')}</p>
        {modeError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{modeError}</p>}
      </div>

      {/* Phase 15 — Address & geolocation */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.address.title')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.address.hint')}</p>
        <div className="settings-geo-grid">
          <label>
            <span>{t('admin.restaurant.address.lat')}</span>
            <input
              type="number"
              step="any"
              value={latStr}
              placeholder={t('admin.restaurant.address.latPlaceholder')}
              onChange={(e) => setLatStr(e.target.value)}
            />
          </label>
          <label>
            <span>{t('admin.restaurant.address.lng')}</span>
            <input
              type="number"
              step="any"
              value={lngStr}
              placeholder={t('admin.restaurant.address.lngPlaceholder')}
              onChange={(e) => setLngStr(e.target.value)}
            />
          </label>
        </div>
        <button
          type="button"
          className="settings-save-btn"
          disabled={savingGeo}
          onClick={handleSaveGeo}
        >
          {savingGeo ? '…' : t('admin.restaurant.save')}
        </button>
        {geoError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{geoError}</p>}
      </div>

      {/* Phase 15 — Opening hours */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.openingHours.title')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.openingHours.hint')}</p>
        <div className="settings-hours-list">
          {DAYS.map((day) => {
            const v = hours[day];
            const isOpen = v != null;
            return (
              <div key={day} className="settings-hours-row">
                <span className="settings-hours-day">{t('admin.restaurant.openingHours.days.' + day)}</span>
                <label className="settings-hours-toggle">
                  <input
                    type="checkbox"
                    checked={isOpen}
                    onChange={(e) => toggleDayOpen(day, e.target.checked)}
                  />
                  <span>{isOpen ? t('admin.restaurant.openingHours.openLabel') : t('admin.restaurant.openingHours.closedLabel')}</span>
                </label>
                {isOpen && (
                  <>
                    <input
                      type="time"
                      value={v?.open || '09:00'}
                      onChange={(e) => setDayTime(day, 'open', e.target.value)}
                    />
                    <input
                      type="time"
                      value={v?.close || '22:00'}
                      onChange={(e) => setDayTime(day, 'close', e.target.value)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="settings-save-btn"
          disabled={savingHours}
          onClick={handleSaveHours}
        >
          {savingHours ? '…' : t('admin.restaurant.save')}
        </button>
        {hoursError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{hoursError}</p>}
      </div>

      {/* Phase 15 — Dine-in toggle */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.dineIn.title')}</h3>
        <label className="settings-toggle-row">
          <input
            type="checkbox"
            checked={dineIn}
            disabled={savingDineIn}
            onChange={(e) => handleToggleDineIn(e.target.checked)}
          />
          <span style={{ color: '#fff' }}>{t('admin.restaurant.dineIn.label')}</span>
        </label>
        <p className="settings-upload-hint">{t('admin.restaurant.dineIn.hint')}</p>
      </div>

      {/* Phase 15 — Pre-order toggle */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.preorder.title')}</h3>
        <label className="settings-toggle-row">
          <input
            type="checkbox"
            checked={preorder}
            disabled={savingPreorder}
            onChange={(e) => handleTogglePreorder(e.target.checked)}
          />
          <span style={{ color: '#fff' }}>{t('admin.restaurant.preorder.label')}</span>
        </label>
        <p className="settings-upload-hint">{t('admin.restaurant.preorder.hint')}</p>
      </div>

      {/* Phase 16 — Cuisine */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.cuisineLabel')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.cuisineHelper')}</p>
        <div className="sp-field-row">
          <select
            className="sp-field-input"
            value={cuisineDraft}
            disabled={cuisineSaving}
            onChange={(e) => setCuisineDraft(e.target.value)}
          >
            <option value="">{t('admin.restaurant.cuisineUnset')}</option>
            {CUISINE_KEYS.map((k) => (
              <option key={k} value={k}>{t(`cuisine.${k}`)}</option>
            ))}
          </select>
          <button
            type="button"
            className="settings-save-btn"
            disabled={cuisineSaving || cuisineDraft === (currentCuisine ?? '')}
            onClick={saveCuisine}
          >
            {cuisineSaving ? '…' : t('admin.restaurant.save')}
          </button>
        </div>
        {cuisineError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{cuisineError}</p>}
      </div>

      {/* Phase 16 — Timezone */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.timezoneLabel')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.timezoneHelper')}</p>
        <div className="sp-field-row">
          <input
            type="text"
            className="sp-field-input"
            value={timezoneDraft}
            placeholder="Asia/Almaty"
            onChange={(e) => { setTimezoneDraft(e.target.value); if (timezoneError) setTimezoneError(null); }}
            onBlur={handleTimezoneBlur}
          />
          <button
            type="button"
            className="settings-save-btn"
            disabled={timezoneSaving || !!timezoneError || timezoneDraft === (currentTimezone ?? '')}
            onClick={saveTimezone}
          >
            {timezoneSaving ? '…' : t('admin.restaurant.save')}
          </button>
        </div>
        <p className="settings-upload-hint">{t('admin.restaurant.timezoneHint')}</p>
        {timezoneError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{timezoneError}</p>}
      </div>

      {/* Phase 17 — Cover image URL */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.coverImageUrlLabel')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.coverImageUrlHint')}</p>
        <div className="sp-field-row">
          <input
            type="url"
            className="sp-field-input"
            value={coverImageUrlDraft}
            placeholder="https://…"
            disabled={savingCoverImageUrl}
            onChange={(e) => setCoverImageUrlDraft(e.target.value)}
          />
          <button
            type="button"
            className="settings-save-btn"
            disabled={savingCoverImageUrl || coverImageUrlDraft === (currentCoverImageUrl ?? '')}
            onClick={saveCoverImageUrl}
          >
            {savingCoverImageUrl ? '…' : t('admin.restaurant.save')}
          </button>
        </div>
        {coverImageUrlError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{coverImageUrlError}</p>}
      </div>

      {/* Phase 17 — isFeatured toggle */}
      <div className="settings-section">
        <h3>{t('admin.restaurant.isFeaturedLabel')}</h3>
        <p className="settings-upload-hint">{t('admin.restaurant.isFeaturedHint')}</p>
        <div className="sp-field-row" style={{ alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            checked={isFeaturedDraft}
            disabled={savingIsFeatured}
            onChange={(e) => setIsFeaturedDraft(e.target.checked)}
          />
          <button
            type="button"
            className="settings-save-btn"
            disabled={savingIsFeatured || isFeaturedDraft === !!currentIsFeatured}
            onClick={saveIsFeatured}
          >
            {savingIsFeatured ? '…' : t('admin.restaurant.save')}
          </button>
        </div>
        {isFeaturedError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{isFeaturedError}</p>}
      </div>

      {/* Phase 18 — tableCount editor */}
      <div className="settings-section">
        <h3>{t('orderType.tableLabel')}</h3>
        <div className="sp-field-row">
          <input
            type="number"
            min={0}
            max={200}
            className="sp-field-input"
            value={tableCountDraft}
            disabled={savingTableCount}
            onChange={(e) => setTableCountDraft(Math.max(0, parseInt(e.target.value, 10) || 0))}
          />
          <button
            type="button"
            className="settings-save-btn"
            disabled={savingTableCount || tableCountDraft === (currentTableCount ?? 0)}
            onClick={handleSaveTableCount}
          >
            {savingTableCount ? '…' : t('admin.restaurant.save')}
          </button>
        </div>
        {tableCountError && <p style={{ color: 'var(--a-red-lighter)', fontSize: 13, marginTop: 8 }}>{tableCountError}</p>}
      </div>
    </div>
  );
}
